<div>
    <div class="card">
        <div class="flex items-start gap-4 flex-wrap">
            <div class="flex-1 min-w-[20rem]">
                <label class="block text-sm font-medium mb-2">Website</label>
                <p-select
                    [options]="realmOptions()"
                    [ngModel]="selectedRealmId()"
                    (ngModelChange)="onRealmChange($event)"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select a website"
                    [filter]="true"
                    filterBy="label"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full"
                ></p-select>
            </div>

            <div class="flex-[2] min-w-[24rem]">
                <label class="block text-sm font-medium mb-2">User ID</label>
                <div class="flex gap-2">
                    <input
                        pInputText
                        type="text"
                        class="flex-1"
                        placeholder="Paste user ID"
                        [ngModel]="userIdInput()"
                        (ngModelChange)="userIdInput.set($event)"
                        (keydown.enter)="lookup()"
                    />
                    <p-button
                        label="Find"
                        icon="pi pi-search"
                        [loading]="looking()"
                        [disabled]="!canLookup()"
                        (click)="lookup()"
                    ></p-button>
                </div>
            </div>
        </div>

        @if (lookupError()) {
            <div class="mt-4">
                <p-message severity="warn" [text]="lookupError()!" styleClass="w-full" />
            </div>
        }
    </div>

    @if (user(); as u) {
        <div class="card">
            <div class="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div class="text-xl font-bold">{{ u.Email ?? '(no email)' }}</div>
                    <div class="text-sm text-muted-color mt-1 flex items-center gap-2">
                        <span class="font-mono">{{ u.Id }}</span>
                        <button
                            type="button"
                            class="p-link"
                            pTooltip="Copy User ID"
                            tooltipPosition="top"
                            (click)="copyUserId()"
                        >
                            <i class="pi pi-copy text-sm"></i>
                        </button>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <p-tag [value]="u.Status" severity="info"></p-tag>
                    <span class="text-sm text-muted-color">
                        Created {{ u.CreatedAt | date: 'medium' }}
                    </span>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Wallet card -->
                <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-3">
                        <div class="font-semibold flex items-center gap-2">
                            <i class="pi pi-wallet"></i> Wallet
                        </div>
                        <span class="text-xs text-muted-color">
                            v{{ u.Wallet.Version }} · updated {{ u.Wallet.UpdatedAt | date: 'short' }}
                        </span>
                    </div>

                    <div class="grid grid-cols-2 gap-3 mb-4">
                        <div class="rounded-md border border-surface-200 dark:border-surface-700 p-3">
                            <div class="text-xs text-muted-color">Free credits</div>
                            <div class="text-2xl font-semibold">{{ u.Wallet.Funds.FreeCredits }}</div>
                        </div>
                        <div class="rounded-md border border-surface-200 dark:border-surface-700 p-3">
                            <div class="text-xs text-muted-color">Paid credits</div>
                            <div class="text-2xl font-semibold">{{ u.Wallet.Funds.PaidCredits }}</div>
                        </div>
                    </div>

                    <div class="flex gap-2">
                        <p-button label="Top up" icon="pi pi-plus" severity="success"
                                  (click)="openWalletDialog('topup')"></p-button>
                        <p-button label="Debit" icon="pi pi-minus" severity="danger"
                                  [outlined]="true" (click)="openWalletDialog('debit')"></p-button>
                    </div>
                </div>

                <!-- KYC card -->
                <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-3">
                        <div class="font-semibold flex items-center gap-2">
                            <i class="pi pi-id-card"></i> KYC
                        </div>
                        @if (kycLoading()) {
                            <i class="pi pi-spin pi-spinner text-sm"></i>
                        }
                    </div>

                    <div class="flex items-center gap-3 mb-3">
                        <p-tag [value]="kycStatusLabel(kycStatus())" [severity]="kycSeverity(kycStatus())"></p-tag>
                        @if (kycStatus()?._type === 'rejected') {
                            <span class="text-sm text-muted-color">
                                Reason: {{ $any(kycStatus()).Reason }}
                            </span>
                        }
                    </div>

                    @if (kycError()) {
                        <div class="mb-3">
                            <p-message severity="error" [text]="kycError()!" styleClass="w-full" />
                        </div>
                    }

                    <div class="flex flex-wrap gap-2">
                        <p-button label="Approve" icon="pi pi-check" severity="success"
                                  [disabled]="kycSaving() || kycStatus()?._type === 'approved'"
                                  (click)="openKycDialog('approve')"></p-button>
                        <p-button label="Reject" icon="pi pi-times" severity="danger"
                                  [disabled]="kycSaving()"
                                  (click)="openKycDialog('reject')"></p-button>
                        <p-button label="Reset to pending" icon="pi pi-refresh" severity="secondary"
                                  [outlined]="true"
                                  [disabled]="kycSaving() || kycStatus()?._type === 'pending'"
                                  (click)="openKycDialog('reset')"></p-button>
                    </div>
                </div>
            </div>
        </div>
    }
</div>

<!-- Wallet adjust dialog -->
<p-dialog
    [visible]="walletDialogVisible()"
    (visibleChange)="walletDialogVisible.set($event)"
    [modal]="true"
    [closable]="!walletSaving()"
    [style]="{ width: '32rem' }"
    [header]="walletMode() === 'topup' ? 'Top up wallet' : 'Debit wallet'"
>
    <div class="flex flex-col gap-4">
        <div class="text-sm text-muted-color">
            Enter positive amounts. Debit will subtract from the user's balance.
        </div>

        <div>
            <label class="block text-sm font-medium mb-2">Free credits</label>
            <p-inputNumber
                [ngModel]="walletFree()"
                (ngModelChange)="walletFree.set($event ?? 0)"
                [min]="0"
                styleClass="w-full"
                inputStyleClass="w-full"
            ></p-inputNumber>
        </div>

        <div>
            <label class="block text-sm font-medium mb-2">Paid credits</label>
            <p-inputNumber
                [ngModel]="walletPaid()"
                (ngModelChange)="walletPaid.set($event ?? 0)"
                [min]="0"
                styleClass="w-full"
                inputStyleClass="w-full"
            ></p-inputNumber>
        </div>

        <div>
            <label class="block text-sm font-medium mb-2">Reason</label>
            <textarea
                pTextarea
                rows="3"
                class="w-full"
                placeholder="Why is this adjustment being made?"
                [ngModel]="walletReason()"
                (ngModelChange)="walletReason.set($event)"
            ></textarea>
        </div>

        @if (walletError()) {
            <p-message severity="error" [text]="walletError()!" styleClass="w-full" />
        }
    </div>

    <ng-template #footer>
        <p-button label="Cancel" severity="secondary" [text]="true"
                  [disabled]="walletSaving()" (click)="closeWalletDialog()"></p-button>
        <p-button
            [label]="walletMode() === 'topup' ? 'Top up' : 'Debit'"
            [severity]="walletMode() === 'topup' ? 'success' : 'danger'"
            [loading]="walletSaving()"
            (click)="confirmWallet()"
        ></p-button>
    </ng-template>
</p-dialog>

<!-- KYC action dialog -->
<p-dialog
    [visible]="kycDialogVisible()"
    (visibleChange)="kycDialogVisible.set($event)"
    [modal]="true"
    [closable]="!kycSaving()"
    [style]="{ width: '32rem' }"
    [header]="kycDialogHeader()"
>
    <div class="flex flex-col gap-4">
        <div>
            <label class="block text-sm font-medium mb-2">Internal reason</label>
            <textarea
                pTextarea
                rows="3"
                class="w-full"
                placeholder="Why is this status change being made? (audit log, not shown to the user)"
                [ngModel]="kycInternalReason()"
                (ngModelChange)="kycInternalReason.set($event)"
            ></textarea>
        </div>

        @if (kycAction() === 'reject') {
            <div>
                <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium">Reason shown to the user</label>
                    <p-button
                        label="Copy from internal"
                        icon="pi pi-copy"
                        severity="secondary"
                        size="small"
                        [text]="true"
                        [disabled]="!kycInternalReason().trim()"
                        (click)="copyInternalToUserReason()"
                    ></p-button>
                </div>
                <textarea
                    pTextarea
                    rows="3"
                    class="w-full"
                    placeholder="This text will be visible to the end user."
                    [ngModel]="kycUserReason()"
                    (ngModelChange)="kycUserReason.set($event)"
                ></textarea>
            </div>
        }

        @if (kycError()) {
            <p-message severity="error" [text]="kycError()!" styleClass="w-full" />
        }
    </div>

    <ng-template #footer>
        <p-button label="Cancel" severity="secondary" [text]="true"
                  [disabled]="kycSaving()" (click)="closeKycDialog()"></p-button>
        <p-button [label]="kycConfirmLabel()" [severity]="kycConfirmSeverity()"
                  [loading]="kycSaving()" (click)="confirmKyc()"></p-button>
    </ng-template>
</p-dialog>

<p-toast />
